// ══════════════════════════════════════════════════
// MÓDULO DE CIRUGÍA — MODELO DE DATOS COMPARTIDO
// Sphere — Gestión Hospitalaria Veterinaria
// ══════════════════════════════════════════════════

// ── Clasificación ASA (estado físico pre-anestésico) ──────────────
export const ASA_OPTIONS = [
  { value: 'I',    label: 'I — Paciente normal sano' },
  { value: 'II',   label: 'II — Enfermedad sistémica leve' },
  { value: 'III',  label: 'III — Enfermedad sistémica grave' },
  { value: 'IV',   label: 'IV — Riesgo de vida' },
  { value: 'V',    label: 'V — Paciente moribundo' },
  { value: 'IE',   label: 'I E — Emergencia, paciente sano' },
  { value: 'IIE',  label: 'II E — Emergencia, enf. leve' },
  { value: 'IIIE', label: 'III E — Emergencia, enf. grave' },
  { value: 'IVE',  label: 'IV E — Emergencia, riesgo vital' },
];

// ── Categorías de procedimientos quirúrgicos ─────────────────────
export const PROCEDURE_CATEGORIES = [
  'Tejidos Blandos',
  'Ortopedia',
  'Dental',
  'Oftalmología',
  'Neurología',
  'Oncología',
  'Emergencia',
  'Otro',
];

// ── Prioridad quirúrgica ──────────────────────────────────────────
export const SURGERY_PRIORITY = {
  Electiva:   { label: 'Electiva',   color: '#0F6E56', bg: '#E1F5EE' },
  Urgente:    { label: 'Urgente',    color: '#C2410C', bg: '#FFF3E0' },
  Emergencia: { label: 'Emergencia', color: '#991B1B', bg: '#FEE2E2', pulse: true },
};

// ── Estados de una solicitud/derivación de cirugía ────────────────
export const SURGERY_REQUEST_STATUS = {
  pending:   { label: 'Pendiente',  color: '#92400E', bg: '#FEF3C7', border: '#F59E0B' },
  scheduled: { label: 'Agendada',   color: '#1E40AF', bg: '#DBEAFE', border: '#3B82F6' },
  completed: { label: 'Completada', color: '#065F46', bg: '#D1FAE5', border: '#10B981' },
  rejected:  { label: 'Rechazada',  color: '#991B1B', bg: '#FEE2E2', border: '#DC2626' },
  cancelled: { label: 'Cancelada',  color: '#6B7280', bg: '#F3F4F6', border: '#9CA3AF' },
};

// ── Exámenes preoperatorios estándar ──────────────────────────────
export const PREOP_EXAMS = [
  { key: 'hemograma',  label: 'Hemograma completo' },
  { key: 'bioquimico', label: 'Perfil bioquímico' },
  { key: 'coagulacion', label: 'Panel de coagulación' },
  { key: 'rayosX',     label: 'Radiografía' },
  { key: 'ecografia',  label: 'Ecografía' },
  { key: 'ecg',        label: 'Electrocardiograma' },
];

// ── Estado inicial del formulario de derivación ───────────────────
export const INIT_SURGERY_REQUEST = {
  // Paciente / tutor (se precargan desde la ficha)
  patientId: '', patientName: '', patientSpecies: '', patientBreed: '',
  patientAge: '', patientWeight: '', patientSex: '',
  ownerId: '', ownerName: '', ownerPhone: '', ownerEmail: '',
  branchId: '',
  // Médico que deriva
  requestingVetId: '', requestingVetName: '',
  // Datos clínicos de la cirugía
  proposedProcedure: '',
  procedureCategory: 'Tejidos Blandos',
  priority: 'Electiva',
  laterality: '',
  clinicalDiagnosis: '',
  clinicalSummary: '',
  indication: '',
  asaScore: '',
  comorbidities: '',
  currentMedications: '',
  allergies: '',
  preOpExams: {
    hemograma: false, bioquimico: false, coagulacion: false,
    rayosX: false, ecografia: false, ecg: false,
  },
  estimatedDurationMin: 60,
  specialRequirements: '',
  fastingHours: 12,
  consentObtained: false,
  preferredDateFrom: '',
  preferredDateTo: '',
  notes: '',
  status: 'pending',
};

/**
 * Devuelve la configuración de estado de una solicitud.
 */
export const getRequestStatusConfig = (status) =>
  SURGERY_REQUEST_STATUS[status] || SURGERY_REQUEST_STATUS.pending;

/**
 * Devuelve la configuración de prioridad quirúrgica.
 */
export const getPriorityConfig = (priority) =>
  SURGERY_PRIORITY[priority] || SURGERY_PRIORITY.Electiva;

/**
 * Convierte una solicitud de cirugía en los datos base para
 * precargar el formulario del scheduler (colección `surgeries`).
 * @param {Object} req - documento de surgeryRequests
 * @returns {Object} datos parciales para setFormData del scheduler
 */
export const requestToSurgeryForm = (req) => {
  const exams = req.preOpExams || {};
  const descriptionParts = [];
  if (req.clinicalDiagnosis) descriptionParts.push(`Diagnóstico: ${req.clinicalDiagnosis}`);
  if (req.indication) descriptionParts.push(`Indicación: ${req.indication}`);
  if (req.clinicalSummary) descriptionParts.push(`Resumen: ${req.clinicalSummary}`);

  return {
    patientId: req.patientId || '',
    patientName: req.patientName || '',
    patientAge: req.patientAge || '',
    patientWeight: req.patientWeight || '',
    patientSpecies: req.patientSpecies || '',
    patientBreed: req.patientBreed || '',
    patientAlergias: req.allergies || '',
    ownerName: req.ownerName || '',
    ownerPhone: req.ownerPhone || '',
    ownerEmail: req.ownerEmail || '',
    priority: req.priority || 'Electiva',
    asaScore: req.asaScore || '',
    procedureType: req.proposedProcedure || '',
    procedureCategory: req.procedureCategory || 'Tejidos Blandos',
    duration: Number(req.estimatedDurationMin) || 60,
    budgetNumber: req.presupuestoNumber || '',
    presupuestoId: req.presupuestoId || '',
    description: descriptionParts.join('\n'),
    equipment: req.specialRequirements || '',
    branchId: req.branchId || '',
    fasting: {
      confirmed: false,
      hours: Number(req.fastingHours) || 12,
    },
    preOpExams: {
      hemograma: !!exams.hemograma,
      bioquimico: !!exams.bioquimico,
      rayosX: !!exams.rayosX,
      ecografia: !!exams.ecografia,
      ecg: !!exams.ecg,
    },
    consentSigned: !!req.consentObtained,
  };
};
